import cn from 'classnames';
import React, { ForwardedRef, forwardRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './hamburger-button.module.scss';

const BurgerIconRectProps = {
  x: 0,
  rx: 0.44,
  ry: 0.44,
  width: 20.29,
  height: 0.88,
  className: styles.hamburgerBar,
} as const;
const BurgerIcon = (
  <svg
    role="presentation"
    className={styles.icon}
    fill="currentColor"
    stroke="currentColor"
    viewBox="0 0 20.29 15"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect y={0.12} {...BurgerIconRectProps}></rect>
    <rect y={6.94} {...BurgerIconRectProps}></rect>
    <rect y={14} {...BurgerIconRectProps}></rect>
  </svg>
);

type ControlledAttributes = 'aria-expanded';

export type HamburgerButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  ControlledAttributes
> & {
  readonly toggled: boolean;
  onToggle?(state?: boolean): void;
};

const HamburgerButton = forwardRef(
  (
    {
      toggled = false,
      onToggle,
      onClick,
      className,
      type = 'button',
      ...props
    }: HamburgerButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const { t } = useTranslation();
    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented) {
          onToggle?.();
        }
      },
      [onToggle, onClick],
    );

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          styles.button,
          { [styles.isToggled]: toggled },
          className,
        )}
        aria-label={t('main_menu')}
        aria-expanded={toggled}
        onClick={handleClick}
        {...props}
      >
        {BurgerIcon}
      </button>
    );
  },
);

HamburgerButton.displayName = 'HamburgerButton';

export default HamburgerButton;
